var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { CHAT_MESSAGE_THRESHOLD_MIN, CHAT_MESSAGE_THRESHOLD_MAX, } from 'root/src/shared/util/calculateChatShareInterval';
import { Platform } from 'root/src/shared/@types/platforms';
import { ScopeSet } from 'root/src/shared/@types/auth';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { Select } from 'root/src/client/v2/common/components/ui/select';
import { Switch } from 'root/src/client/v2/common/components/ui/switch';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { RedesignChatAutoShareHeader } from 'root/src/client/v2/common/components/RedesignChatShare/RedesignChatAutoShareHeader';
import { RedesignChatMessagePreview } from 'root/src/client/v2/common/components/RedesignChatShare/RedesignChatMessagePreview';
import { RedesignChatPermissionModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignChatPermissionModal';
const THRESHOLD_PRESETS = [20, 50, 100, 200, 500];
const TOAST_ID = 'chat-link-sharing';
const clampThreshold = (value) => Math.min(CHAT_MESSAGE_THRESHOLD_MAX, Math.max(CHAT_MESSAGE_THRESHOLD_MIN, value !== null && value !== void 0 ? value : 20));
const INTERVAL_MODE_OPTIONS = [
    { value: 'auto', label: 'Auto (Recommended)' },
    { value: '30', label: 'Every 30 minutes' },
    {
        value: 'chatMessages',
        label: (<span className="flex items-center gap-2">
				Based on chat activity
				<span className="rounded-full bg-neon-cyan/15 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-neon-cyan">New</span>
			</span>),
        searchText: 'Based on chat activity',
    },
];
// Redesign port of the legacy SponsorshipDetailsModal auto-share block: toggle chat link sharing
// on/off, pick the posting interval mode (auto / every 30 min / after N chat messages) and preview
// the announcement. Persists through the same tRPC procedures the legacy modal uses
// (project.getChatLinkSharing / project.setChatLinkSharing). Render only when the share url exists.
export const RedesignChatAutoShareSection = ({ dropId, shareUrl, linkMarketingCopy }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const modal = useRedesignModal();
    const utils = trpc.useUtils();
    const reducedMotion = useReducedMotion();
    const saveThresholdAction = useAsyncAction();
    const chatLinkSharingQuery = trpc.project.getChatLinkSharing.useQuery({ dropId });
    const hasValidScopeQuery = trpc.auth.hasValidTokenScope.useQuery({ platform: Platform.Twitch, scopeSet: ScopeSet.creatorPlus });
    const [chatMessageThreshold, setChatMessageThreshold] = useState((_b = (_a = chatLinkSharingQuery.data) === null || _a === void 0 ? void 0 : _a.chatMessageThreshold) !== null && _b !== void 0 ? _b : 20);
    useEffect(() => {
        var _a;
        if ((_a = chatLinkSharingQuery.data) === null || _a === void 0 ? void 0 : _a.chatMessageThreshold) {
            setChatMessageThreshold(chatLinkSharingQuery.data.chatMessageThreshold);
        }
    }, [(_c = chatLinkSharingQuery.data) === null || _c === void 0 ? void 0 : _c.chatMessageThreshold]);
    // Optimistic cache update + rollback (mirrors the legacy modal) so the switch and the interval
    // select respond instantly instead of waiting on the round trip.
    const setChatLinkSharingMutation = trpc.project.setChatLinkSharing.useMutation({
        onMutate: (variables) => __awaiter(void 0, void 0, void 0, function* () {
            yield utils.project.getChatLinkSharing.cancel({ dropId });
            const previous = utils.project.getChatLinkSharing.getData({ dropId });
            utils.project.getChatLinkSharing.setData({ dropId }, old => {
                var _a, _b, _c, _d;
                return ({
                    enabled: variables.enabled,
                    intervalMode: (_b = (_a = variables.intervalMode) !== null && _a !== void 0 ? _a : old === null || old === void 0 ? void 0 : old.intervalMode) !== null && _b !== void 0 ? _b : 'auto',
                    autoIntervalMinutes: (_c = old === null || old === void 0 ? void 0 : old.autoIntervalMinutes) !== null && _c !== void 0 ? _c : 60,
                    chatMessageThreshold: (_d = variables.chatMessageThreshold) !== null && _d !== void 0 ? _d : old === null || old === void 0 ? void 0 : old.chatMessageThreshold,
                });
            });
            return { previous };
        }),
        onSuccess: (_, variables, context) => {
            var _a;
            const wasAlreadyEnabled = Boolean((_a = context === null || context === void 0 ? void 0 : context.previous) === null || _a === void 0 ? void 0 : _a.enabled);
            const message = !variables.enabled
                ? 'Chat link sharing disabled'
                : wasAlreadyEnabled ? 'Chat link sharing updated' : 'Chat link sharing enabled';
            toast.success(message, { id: TOAST_ID });
        },
        onError: (_, __, context) => {
            utils.project.getChatLinkSharing.setData({ dropId }, context === null || context === void 0 ? void 0 : context.previous);
            toast.error('Failed to update chat link sharing', { id: TOAST_ID });
        },
        onSettled: () => {
            void utils.project.getChatLinkSharing.invalidate({ dropId });
        },
    });
    const isEnabled = (_e = (_d = chatLinkSharingQuery.data) === null || _d === void 0 ? void 0 : _d.enabled) !== null && _e !== void 0 ? _e : false;
    const intervalMode = (_g = (_f = chatLinkSharingQuery.data) === null || _f === void 0 ? void 0 : _f.intervalMode) !== null && _g !== void 0 ? _g : 'auto';
    const isThresholdValid = chatMessageThreshold !== undefined
        && chatMessageThreshold >= CHAT_MESSAGE_THRESHOLD_MIN && chatMessageThreshold <= CHAT_MESSAGE_THRESHOLD_MAX;
    const enableSharing = (mode) => {
        setChatLinkSharingMutation.mutate(Object.assign({ dropId, enabled: true, intervalMode: mode }, (mode === 'chatMessages' && { chatMessageThreshold: clampThreshold(chatMessageThreshold) })));
    };
    const onToggle = (checked) => {
        if (!checked) {
            setChatLinkSharingMutation.mutate({ dropId, enabled: false });
            return;
        }
        if (hasValidScopeQuery.data) {
            enableSharing(intervalMode);
            return;
        }
        // The permission modal replaces this one (single redesign modal slot), matching the legacy
        // flow: grant -> enable + close, cancel -> close.
        modal.show(<RedesignChatPermissionModal onGranted={() => {
                enableSharing(intervalMode);
                modal.hide();
            }} onCancel={modal.hide}/>);
    };
    const onSaveThreshold = () => {
        void saveThresholdAction.run(() => setChatLinkSharingMutation.mutateAsync({
            dropId,
            enabled: true,
            intervalMode: 'chatMessages',
            chatMessageThreshold: clampThreshold(chatMessageThreshold),
        })).catch(() => {
            // The mutation's onError already toasts; the button error state is the local feedback.
        });
    };
    return (<div className="flex w-full flex-col gap-3 rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4">
			<RedesignChatAutoShareHeader />
			<div className="flex w-full flex-col">
				<div className="flex w-full items-center justify-between gap-3">
					<label htmlFor="chat-auto-share" className="cursor-pointer select-none text-sm text-foreground">Auto-share link in chat</label>
					<Switch id="chat-auto-share" checked={isEnabled} onCheckedChange={onToggle} disabled={chatLinkSharingQuery.isLoading}/>
				</div>
				<AnimatePresence initial={false}>
					{isEnabled && (<motion.div key="interval-controls" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: reducedMotion ? 0 : 0.2, ease: 'easeOut' }} className="w-full overflow-hidden">
							<div className="flex w-full flex-col items-center gap-3 pt-3">
								<Select value={intervalMode} onValueChange={enableSharing} options={INTERVAL_MODE_OPTIONS} disabled={setChatLinkSharingMutation.isPending}/>
								{intervalMode === 'auto' && (<p className="text-center text-xs text-muted-foreground">
										Adjusts based on your live viewer count to maximize reach while preventing spam (every 20 to 60 minutes)
									</p>)}
								{intervalMode !== 'chatMessages' && (<TooltipProvider delayDuration={200}>
										<Tooltip>
											<TooltipTrigger asChild>
												<span className="flex flex-wrap items-center justify-center gap-1 text-center text-xs font-bold text-neon-cyan">
													<AutoAwesomeIcon sx={{ fontSize: 14 }}/>
													New: Share your link based on chat activity,
													<button type="button" className="cursor-pointer border-none bg-transparent p-0 text-xs font-bold text-neon-cyan underline" onClick={() => enableSharing('chatMessages')}>
														try it!
													</button>
												</span>
											</TooltipTrigger>
											<TooltipContent className="max-w-[240px] text-left">
												Automatically post your link based on how active your chat is.
												Set a message count threshold and we will post when it is reached.
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>)}
								{intervalMode === 'chatMessages' && (<div className="flex w-full flex-col items-center gap-3">
										<p className="text-center text-xs text-muted-foreground">Post an announcement after this many chat messages</p>
										<div className="flex flex-wrap justify-center gap-2">
											{THRESHOLD_PRESETS.map(preset => (<button key={preset} type="button" onClick={() => setChatMessageThreshold(preset)} className={cn('cursor-pointer rounded-lg border border-solid px-3 py-1 text-xs transition-colors', chatMessageThreshold === preset
                        ? 'border-primary bg-primary text-white'
                        : 'border-white/[0.12] bg-transparent text-foreground hover:border-primary/60')}>
													{preset}
												</button>))}
										</div>
										<div className="flex items-center gap-2">
											<Input type="number" aria-label="Custom chat message threshold" placeholder="Custom" value={chatMessageThreshold !== null && chatMessageThreshold !== void 0 ? chatMessageThreshold : ''} min={CHAT_MESSAGE_THRESHOLD_MIN} max={CHAT_MESSAGE_THRESHOLD_MAX} onChange={(event) => {
                    const raw = event.target.value;
                    setChatMessageThreshold(raw === '' ? undefined : Number(raw));
                }} onBlur={() => setChatMessageThreshold(clampThreshold(chatMessageThreshold))} className="w-24"/>
											<Button variant="brand" size="sm" onClick={onSaveThreshold} loading={saveThresholdAction.loading} success={saveThresholdAction.success} error={saveThresholdAction.error} disabled={!isThresholdValid} className="rounded-lg text-xs font-semibold">
												Save
											</Button>
										</div>
										<p className="text-center text-xs text-muted-foreground">
											Posts an announcement after every {chatMessageThreshold !== null && chatMessageThreshold !== void 0 ? chatMessageThreshold : clampThreshold(chatMessageThreshold)} chat
											messages during your stream
										</p>
									</div>)}
							</div>
						</motion.div>)}
				</AnimatePresence>
			</div>
			<RedesignChatMessagePreview linkMarketingCopy={linkMarketingCopy} url={shareUrl}/>
		</div>);
};
